<template>
  <v-dialog v-model="dialog" max-width="1200px" persistent width="90%" :retain-focus="false">
    <v-card>
      <v-card-title class="headline primary white--text">
        <v-icon left color="white">mdi-upload</v-icon>
        Import Leads
        <v-spacer></v-spacer>
        <v-btn icon dark @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-6">
        <v-row>
          <!-- Left Column: Template Info -->
          <v-col cols="4">
            <v-card outlined class="mb-4">
              <v-card-title class="subtitle-1">
                <v-icon left>mdi-information</v-icon>
                Import Instructions
              </v-card-title>
              <v-card-text>
                <v-btn 
                  small 
                  color="primary" 
                  @click="downloadTemplate"
                  block
                  class="mb-3"
                  :disabled="importing"
                >
                  <v-icon left small>mdi-download</v-icon>
                  Download Template
                </v-btn>
                
                <div class="text-caption text--secondary mb-3">
                  <strong>Required columns:</strong> Firma, Nachname, Vorname
                </div>
                
                <v-alert type="info" dense outlined class="mb-0">
                  <small>
                    • Supports CSV and Excel files<br>
                    • Duplicates are skipped automatically<br>
                    • Shows real-time progress
                  </small>
                </v-alert>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Right Column: Upload & Progress -->
          <v-col cols="8">
            <!-- Upload Section - Always visible -->
            <v-card outlined class="mb-4">
              <v-card-title class="subtitle-1">
                <v-icon left>mdi-file-upload</v-icon>
                Upload Your File
              </v-card-title>
              <v-card-text>
                <v-file-input
                  v-model="file"
                  label="Drag & drop or click to browse"
                  accept=".csv,.xls,.xlsx"
                  prepend-icon="mdi-file"
                  show-size
                  outlined
                  clearable
                  @change="resetResults"
                  :disabled="importing"
                ></v-file-input>
                
                <v-alert v-if="error" type="error" dense outlined class="mt-3">
                  {{ error }}
                </v-alert>
              </v-card-text>
            </v-card>

            <!-- Results Card (shown after import complete) -->
            <v-card v-if="importComplete && results" outlined class="mb-4" color="success" dark>
              <v-card-title class="subtitle-1 white--text">
                <v-icon left>mdi-check-circle</v-icon>
                Import Complete!
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="3">
                    <div class="text-center">
                      <div class="text-h4">{{ results.imported }}</div>
                      <div class="text-caption">Imported</div>
                    </div>
                  </v-col>
                  <v-col cols="3">
                    <div class="text-center">
                      <div class="text-h4">{{ results.skipped }}</div>
                      <div class="text-caption">Skipped</div>
                    </div>
                  </v-col>
                  <v-col cols="3">
                    <div class="text-center">
                      <div class="text-h4">{{ results.errors }}</div>
                      <div class="text-caption">Errors</div>
                    </div>
                  </v-col>
                  <v-col cols="3">
                    <div class="text-center">
                      <div class="text-h4">{{ results.total }}</div>
                      <div class="text-caption">Total</div>
                    </div>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- Progress Section (shown during import) -->
            <div v-if="importing && !importComplete">
              <v-card outlined class="mb-4">
                <v-card-title class="subtitle-1">
                  <v-icon left color="primary">mdi-progress-clock</v-icon>
                  Importing Leads...
                </v-card-title>
                
                <v-card-text>
                  <!-- Main Progress Bar -->
                  <div class="mb-6">
                    <div class="d-flex justify-space-between mb-2">
                      <span class="text-caption">
                        Processing row {{ progress.current }} of {{ progress.total }}
                        <span v-if="progress.current > 0 && progress.total > 0">
                          ({{ Math.round((progress.current / progress.total) * 100) }}%)
                        </span>
                      </span>
                      <span class="text-caption">{{ Math.round(progress.percentage) }}%</span>
                    </div>
                    
                    <v-progress-linear
                      v-model="progress.percentage"
                      height="20"
                      color="primary"
                      striped
                      :indeterminate="progress.total === 0"
                    >
                      <template v-if="progress.total > 0">
                        <strong>{{ Math.round(progress.percentage) }}%</strong>
                      </template>
                      <template v-else>
                        <strong>Preparing...</strong>
                      </template>
                    </v-progress-linear>
                  </div>

                  <!-- Real-time Statistics -->
                  <v-row class="mb-4">
                    <v-col cols="3">
                      <v-card outlined>
                        <v-card-text class="text-center pa-3">
                          <div class="text-h5">{{ stats.imported }}</div>
                          <div class="text-caption">Imported</div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                    <v-col cols="3">
                      <v-card outlined>
                        <v-card-text class="text-center pa-3">
                          <div class="text-h5">{{ stats.skipped }}</div>
                          <div class="text-caption">Skipped</div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                    <v-col cols="3">
                      <v-card outlined>
                        <v-card-text class="text-center pa-3">
                          <div class="text-h5">{{ stats.errors }}</div>
                          <div class="text-caption">Errors</div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                    <v-col cols="3">
                      <v-card outlined>
                        <v-card-text class="text-center pa-3">
                          <div class="text-h5">{{ stats.total }}</div>
                          <div class="text-caption">Total</div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>

                  <!-- Progress Logs -->
                  <v-card outlined>
                    <v-card-title class="subtitle-2">
                      <v-icon left small>mdi-text-box</v-icon>
                      Import Log
                      <v-chip v-if="logs.length > 0" x-small class="ml-2">
                        {{ logs.length }} entries
                      </v-chip>
                    </v-card-title>
                    <v-divider></v-divider>
                    <v-card-text class="pa-0">
                      <div class="progress-logs" style="max-height: 200px; overflow-y: auto; padding: 8px;">
                        <div 
                          v-for="(log, index) in logs" 
                          :key="index"
                          class="log-entry pa-2"
                          :class="logClass(log.type)"
                        >
                          <div class="d-flex align-center">
                            <v-icon 
                              x-small 
                              :color="logColor(log.type)"
                              class="mr-2"
                            >
                              {{ logIcon(log.type) }}
                            </v-icon>
                            <span class="text-caption">{{ log.message }}</span>
                            <span class="text-caption text--secondary ml-auto" style="font-size: 10px;">
                              {{ log.time }}
                            </span>
                          </div>
                        </div>
                        <div v-if="logs.length === 0" class="text-center py-4 text--secondary">
                          Waiting for import to start...
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-card-text>
              </v-card>
            </div>

            <!-- Logs Card (shown after import complete) -->
            <v-card v-if="importComplete && logs.length > 0" outlined>
              <v-card-title class="subtitle-2">
                <v-icon left small>mdi-text-box</v-icon>
                Import Log
                <v-chip x-small class="ml-2">{{ logs.length }} entries</v-chip>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="pa-0">
                <div class="progress-logs" style="max-height: 200px; overflow-y: auto; padding: 8px;">
                  <div 
                    v-for="(log, index) in logs" 
                    :key="index"
                    class="log-entry pa-2"
                    :class="logClass(log.type)"
                  >
                    <div class="d-flex align-center">
                      <v-icon 
                        x-small 
                        :color="logColor(log.type)"
                        class="mr-2"
                      >
                        {{ logIcon(log.type) }}
                      </v-icon>
                      <span class="text-caption">{{ log.message }}</span>
                      <span class="text-caption text--secondary ml-auto" style="font-size: 10px;">
                        {{ log.time }}
                      </span>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        
        <!-- Show during import (not complete) -->
        <template v-if="importing && !importComplete">
          <v-btn text color="warning" @click="continueInBackground">
            <v-icon left>mdi-window-minimize</v-icon>
            Minimize
          </v-btn>
          <v-btn color="error" @click="cancelImport" :loading="cancelling">
            <v-icon left>mdi-cancel</v-icon>
            Cancel
          </v-btn>
        </template>
        
        <!-- Show when not importing or when complete -->
        <template v-else>
          <v-btn text @click="closeDialog">
            <v-icon left>mdi-close</v-icon>
            {{ importComplete ? 'Close' : 'Cancel' }}
          </v-btn>
          
          <!-- Start Import button (when file selected and not importing) -->
          <v-btn
            v-if="!importing && !importComplete"
            color="primary"
            @click="startImport"
            :disabled="!file"
          >
            <v-icon left>mdi-upload</v-icon>
            Start Import
          </v-btn>
          
          <!-- Import Another button (after successful import) -->
          <v-btn
            v-if="importComplete"
            color="primary"
            @click="resetForm"
          >
            <v-icon left>mdi-plus</v-icon>
            Import Another
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'LeadImportDialog',
  props: {
    value: { type: Boolean, default: false }
  },
  data() {
    return {
      dialog: false,
      file: null,
      importing: false,
      importComplete: false,
      cancelling: false,
      error: '',
      results: null,
      backgroundImport: false,
      backgroundImportId: null,
      showBackgroundNotification: false,
      notificationSound: null,
      progress: { current: 0, total: 0, percentage: 0 },
      stats: { imported: 0, skipped: 0, errors: 0, total: 0 },
      logs: [],
      pollInterval: null,
      importId: null,
      backgroundPollInterval: null,
      templateColumns: [
    
        { name: 'Kundengruppe', example: 'Autohäuser' },
        { name: 'Kundentyp', example: 'Autohaus' },             
        { name: 'Vertragswerkstatt', example: 'VW' },
        { name: 'Firma', example: 'Müller GmbH' },
        { name: 'Nachname', example: 'Müller' },
        { name: 'Vorname', example: 'Hans' },
        { name: 'Telefon', example: '+49 123 456789' },
        { name: 'Email', example: 'info@firma.de' },
        { name: 'Straße', example: 'Hauptstraße 123' },
        { name: 'PLZ', example: '12345' },
        { name: 'Stadt', example: 'Berlin' },
        { name: 'Bemerkungen', example: 'Interested' },
        
      ]
    }
  },
  watch: {
    value(val) {
      console.log('Dialog value changed from parent:', val, 'current importing:', this.importing)
      if (!val && this.importing && !this.importComplete) {
        console.log('Preventing parent from closing dialog - import in progress')
       
        this.$nextTick(() => {
          this.$emit('input', true)
        })
        return
      }
      this.dialog = val
    },
    dialog(val) {
      console.log('Dialog state changed:', val, 'importing:', this.importing, 'complete:', this.importComplete)
      
      this.$emit('input', val)
      
      if (!val && (!this.importing || this.importComplete)) {
        this.cleanup()
      }
    }
  },
  created() {
    this.notificationSound = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAAAA==')
    this.checkForActiveImports()
  },
  beforeDestroy() {
    this.cleanup()
  },
  methods: {
    downloadTemplate() {
      const headers = this.templateColumns.map(col => col.name).join(';')
      const exampleRow = this.templateColumns.map(col => `"${col.example}"`).join(';')
      const csv = `${headers}\n${exampleRow}`
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'leads_template.csv'
      link.click()
    },
    resetResults() {
      this.error = ''
      this.results = null
      this.logs = []
    },
    // async startImport() {
    //   console.log('=== START IMPORT ===')
    //   if (!this.file) {
    //     this.error = 'Please select a file first'
    //     return
    //   }
      
    //   console.log('Setting importing to true')
    //   this.importing = true
    //   this.importComplete = false
    //   this.backgroundImport = false
    //   this.error = ''
    //   this.results = null
    //   this.logs = []
    //   this.progress = { current: 0, total: 0, percentage: 0 }
    //   this.stats = { imported: 0, skipped: 0, errors: 0, total: 0 }
      
    //   console.log('Importing state:', this.importing)
    //   this.addLog('Starting import...', 'info')
      
    //   try {
    //     const formData = new FormData()
    //     formData.append('file', this.file)
    //     this.addLog('Uploading file...', 'info')
        
    //     console.log('Sending request to server...')
    //     const response = await fetch('http://localhost:8000/import_leads.php', {
    //       method: 'POST',
    //       body: formData
    //     })
        
    //     console.log('Response received:', response.status)
    //     const contentType = response.headers.get("content-type")
    //     if (!contentType || !contentType.includes("application/json")) {
    //       const text = await response.text()
    //       console.error('Non-JSON response:', text)
    //       throw new Error(`Server error: ${text.substring(0, 100)}`)
    //     }
        
    //     const result = await response.json()
    //     console.log('Server response:', result)
        
    //     if (!result.success) throw new Error(result.message || 'Failed to start import')
        
    //     this.importId = result.import_id
    //     this.backgroundImportId = result.import_id
    //     console.log('Import ID:', this.importId)
        
    //     this.addLog(`Import started: ${this.importId}`, 'info')
    //     this.addLog('Processing file...', 'info')
        
    //     console.log('Starting polling...')
    //     this.startPolling()
    //   } catch (err) {
    //     console.error('Import error:', err)
    //     this.error = err.message || 'Import failed'
    //     this.addLog(`Error: ${this.error}`, 'error')
    //     this.importing = false
    //   }
      
    //   console.log('=== END START IMPORT - importing:', this.importing, '===')
    // },


    // In your Vue component, change the import function:
    async startImport() {
      console.log('=== START IMPORT ===')
      if (!this.file) {
        this.error = 'Please select a file first'
        return
      }
      
      this.importing = true
      this.importComplete = false
      this.backgroundImport = false
      this.error = ''
      this.results = null
      this.logs = []
      this.progress = { current: 0, total: 0, percentage: 0 }
      this.stats = { imported: 0, skipped: 0, errors: 0, total: 0 }
      
      console.log('Importing state:', this.importing)
      this.addLog('Starting import...', 'info')
      
      try {
        const formData = new FormData()
        formData.append('file', this.file)
        
        this.addLog('Uploading file...', 'info')
        
        console.log('Sending request to server...')
        const response = await fetch('https://www.migro.net/reifen/import_leads.php', {
          method: 'POST',
          body: formData
        })
        
        console.log('Response received:', response.status, response.statusText)
        
        // Get the response as text first
        const responseText = await response.text()
        console.log('Raw response:', responseText.substring(0, 300))
        
        // Try to parse as JSON (ignore content-type)
        let result
        try {
          result = JSON.parse(responseText)
          console.log('Parsed JSON:', result)
        } catch (parseError) {
          console.error('JSON parse failed:', parseError)
          console.error('Response that failed:', responseText)
          
          // Check if it's actually HTML
          if (responseText.includes('<html') || responseText.includes('<br') || responseText.includes('PHP Error')) {
            throw new Error('Server returned HTML error page')
          } else {
            throw new Error('Invalid server response format')
          }
        }
        
        if (!result.success) {
          throw new Error(result.message || 'Import failed')
        }
        
        this.importId = result.import_id
        console.log('Import ID:', this.importId)
        
        this.addLog(`Import started: ${this.importId}`, 'info')
        this.addLog('Processing file...', 'info')
        
        console.log('Starting polling...')
        this.startPolling()
        
      } catch (err) {
        console.error('Import error:', err)
        this.error = err.message || 'Import failed'
        this.addLog(`Error: ${this.error}`, 'error')
        this.importing = false
      }
      
      console.log('=== END START IMPORT - importing:', this.importing, '===')
    },  


    startPolling() {
      if (this.pollInterval) clearInterval(this.pollInterval)
      this.pollInterval = setInterval(() => this.checkProgress(), 300)
    },
    async checkProgress() {
      if (!this.importId) return
      try {
        const response = await fetch(`https://www.migro.net/reifen/import_leads.php?action=get_progress&import_id=${this.importId}&t=${Date.now()}`)
        const data = await response.json()
        
        if (!data.success) {
          if (data.status === 'not_found') this.handleImportCompleted()
          return
        }
        
        // Update progress stats
        this.progress.current = data.processed || 0
        this.progress.total = data.total || 0
        this.stats.imported = data.imported || 0
        this.stats.skipped = data.skipped || 0
        this.stats.errors = data.errors || 0
        this.stats.total = data.total || 0
        
        if (this.progress.total > 0) {
          this.progress.percentage = Math.min(100, (this.progress.current / this.progress.total) * 100)
        }
        
        // Handle logs - only add new ones
        if (data.logs && Array.isArray(data.logs)) {
          // Filter out logs we already have
          const newLogs = data.logs.filter(log => {
            return !this.logs.some(existingLog => 
              existingLog.message === log.message && 
              existingLog.time === log.time
            )
          })
          
          // Add new logs to beginning of our array
          newLogs.reverse().forEach(log => {
            this.logs.unshift({
              message: log.message,
              type: log.type,
              time: log.time
            })
          })
          
          // Keep only last 30 logs
          if (this.logs.length > 30) {
            this.logs = this.logs.slice(0, 30)
          }
        }
        
        // Handle import completion
        if (data.status === 'complete') {
          this.finishImport(data)
        } else if (data.status === 'error') {
          this.handleImportError(data.message || 'Import failed')
        } else if (data.status === 'cancelled') {
          this.handleImportCancelled()
        }
        
      } catch (err) {
        console.error('Progress error:', err)
      }
    },
    finishImport(data) {
      this.cleanupPolling()
      this.importing = false
      this.importComplete = true
      this.backgroundImport = false
      this.progress.percentage = 100
      this.progress.current = this.stats.total
      
      // Update stats from the final data
      this.stats.imported = data.imported || 0
      this.stats.skipped = data.skipped || 0
      this.stats.errors = data.errors || 0
      this.stats.total = data.total || 0
      
      this.results = {
        imported: this.stats.imported,
        skipped: this.stats.skipped,
        errors: this.stats.errors,
        total: this.stats.total
      }
      
      // Don't add duplicate completion logs here
      // They should already be in the logs from PHP
      
      this.$emit('import-complete')
    },
    continueInBackground() {
      this.backgroundImport = true
      this.cleanupPolling()
      this.startBackgroundPolling()
      this.addLog('Continuing in background...', 'info')
      this.dialog = false
    },
    startBackgroundPolling() {
      if (this.backgroundPollInterval) clearInterval(this.backgroundPollInterval)
      this.backgroundPollInterval = setInterval(() => this.checkBackgroundProgress(), 2000)
    },
    async checkBackgroundProgress() {
      if (!this.backgroundImportId) return
      try {
        const response = await fetch(`https://www.migro.net/reifen/import_leads.php?action=get_progress&import_id=${this.backgroundImportId}&t=${Date.now()}`)
        const data = await response.json()
        if (data.success && (data.status === 'complete' || data.status === 'error' || data.status === 'cancelled')) {
          this.cleanupBackgroundPolling()
          if (this.dialog) this.resumeBackgroundImport()
        }
      } catch (err) {
        console.log('Background check failed:', err)
      }
    },
    cleanupBackgroundPolling() {
      if (this.backgroundPollInterval) {
        clearInterval(this.backgroundPollInterval)
        this.backgroundPollInterval = null
      }
    },
    async checkForActiveImports() {
 
      return
    },
    async resumeBackgroundImport() {
      if (!this.backgroundImportId) return
      this.importId = this.backgroundImportId
      this.importing = true
      this.importComplete = false
      this.backgroundImport = false
      this.addLog('Resuming import...', 'info')
      this.startPolling()
      this.cleanupBackgroundPolling()
    },
    handleImportError(message) {
      this.cleanupPolling()
      this.cleanupBackgroundPolling()
      this.importing = false
      this.backgroundImport = false
      this.error = message || 'Import failed'
      this.addLog(`Failed: ${this.error}`, 'error')
    },
    handleImportCancelled() {
      this.cleanupPolling()
      this.cleanupBackgroundPolling()
      this.importing = false
      this.backgroundImport = false
      this.addLog('Import cancelled', 'skip')
    },
    handleImportCompleted() {
      this.cleanupPolling()
      this.cleanupBackgroundPolling()
      this.importing = false
      this.importComplete = true
      this.backgroundImport = false
      this.progress.percentage = 100
      this.addLog('Import completed', 'info')
    },
    async cancelImport() {
      if (!this.importId) return
      this.cancelling = true
      this.addLog('Cancelling...', 'info')
      try {
        await fetch(`https://www.migro.net/reifen/import_leads.php?action=cancel_import&import_id=${this.importId}`)
        this.cleanupPolling()
        this.cleanupBackgroundPolling()
        this.importing = false
        this.backgroundImport = false
        this.addLog('Cancelled by user', 'skip')
      } catch (err) {
        this.addLog('Failed to cancel', 'error')
      } finally {
        this.cancelling = false
      }
    },
    addLog(message, type = 'info') {
      this.logs.unshift({
        message,
        type,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      })
      if (this.logs.length > 30) this.logs = this.logs.slice(0, 30)
    },
    logClass(type) {
      return { success: 'success-log', error: 'error-log', skip: 'skip-log', info: 'info-log' }[type] || ''
    },
    logColor(type) {
      return { success: 'success', error: 'error', skip: 'warning', info: 'info' }[type] || 'grey'
    },
    logIcon(type) {
      return { success: 'mdi-check-circle', error: 'mdi-alert-circle', skip: 'mdi-skip-next', info: 'mdi-information' }[type] || 'mdi-dots-horizontal'
    },
    resetForm() {
      this.file = null
      this.results = null
      this.error = ''
      this.logs = []
      this.backgroundImport = false
      this.backgroundImportId = null
    },
    cleanupPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
    },
    cleanup() {
      this.cleanupPolling()
      this.cleanupBackgroundPolling()
      this.resetForm()
      this.importing = false
      this.importComplete = false
      this.cancelling = false
      this.importId = null
      this.progress = { current: 0, total: 0, percentage: 0 }
      this.stats = { imported: 0, skipped: 0, errors: 0, total: 0 }
    },
    closeDialog() {
      console.log('closeDialog called - importing:', this.importing, 'complete:', this.importComplete)
      if (!this.importing || this.importComplete) {
        console.log('Closing dialog')
        this.dialog = false
      } else {
        console.warn('Cannot close - import in progress!')
        alert('Import is in progress. Please wait or use "Minimize" to continue in background.')
      }
    }
  }
}
</script>

<style scoped>
.success-log { background-color: rgba(76, 175, 80, 0.08); }
.error-log { background-color: rgba(244, 67, 54, 0.08); }
.skip-log { background-color: rgba(255, 193, 7, 0.08); }
.info-log { background-color: rgba(33, 150, 243, 0.08); }
.log-entry {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: background-color 0.2s;
}
.log-entry:hover { background-color: rgba(0, 0, 0, 0.02); }
.log-entry:last-child { border-bottom: none; }
.progress-logs {
  font-family: 'Roboto Mono', monospace;
  font-size: 12px;
}
</style>